// src/pages/parametres/sauvegarde/Restauration.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Button, Alert, ProgressBar, Modal, Badge, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast, { Toaster } from 'react-hot-toast';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

const showToast = (message, type = 'success') => {
  const toastOptions = {
    duration: 3000,
    position: 'top-right',
    style: {
      borderRadius: '10px',
      background: '#fff',
      color: '#333',
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
      padding: '16px',
      fontSize: '14px',
    },
  };

  switch(type) {
    case 'success':
      toast.success(message, toastOptions);
      break;
    case 'error':
      toast.error(message, toastOptions);
      break;
    case 'warning':
      toast(message, {
        ...toastOptions,
        icon: '⚠️',
        style: { ...toastOptions.style, borderLeft: `4px solid ${COLORS.warning}` }
      });
      break;
    default:
      toast(message, toastOptions);
  }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return format(new Date(dateString), 'dd MMMM yyyy à HH:mm', { locale: fr });
};

export default function Restauration() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [progress, setProgress] = useState(0);
  const [restoreProgress, setRestoreProgress] = useState('');
  const [restoreDetails, setRestoreDetails] = useState(null);
  const [backups, setBackups] = useState([]);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    fetchBackups();
    fetchLogs();
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    setError(null);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const response = await fetch(`https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=list`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setBackups(result.backups || []);
      } else {
        setError(result.message || 'Erreur de chargement');
        showToast(result.message || 'Erreur de chargement', 'error');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const response = await fetch(`https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=logs`, {
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setLogs(result.logs || []);
        }
      }
    } catch (err) {
      console.error('Erreur chargement logs:', err);
    }
  };

  const handlePreview = async (backup) => {
    setSelectedBackup(backup);
    setPreview(null);
    setError(null);
    
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const response = await fetch(`https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=preview&file=${encodeURIComponent(backup.filename)}`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setPreview(result.preview);
      } else {
        setError(result.message || 'Erreur lors du chargement de l\'aperçu');
        showToast(result.message || 'Erreur lors du chargement de l\'aperçu', 'error');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur: ' + err.message);
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const handleRestore = async () => {
    if (!selectedBackup) return;
    
    setShowConfirmModal(false);
    setRestoring(true);
    setProgress(0);
    setRestoreProgress('Préparation de la restauration...');
    setRestoreDetails(null);
    
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 500);

      setRestoreProgress('Restauration des données en cours...');
      
      const response = await fetch(`https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=restore`, {
        method: 'POST',
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file: selectedBackup.filename
        })
      });

      clearInterval(progressInterval);
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setProgress(100);
        setRestoreProgress('Restauration terminée !');
        
        if (result.queries_executed) {
          setRestoreDetails({
            queries: result.queries_executed,
            errors: result.errors || []
          });
        }
        
        showToast('Restauration effectuée avec succès', 'success');
        
        if (result.errors && result.errors.length > 0) {
          showToast(`${result.errors.length} erreur(s) lors de la restauration`, 'warning');
        }
        
        fetchLogs();
        
        setTimeout(() => {
          setRestoring(false);
          setProgress(0);
          setRestoreProgress('');
        }, 2000);
      } else {
        throw new Error(result.message || 'Erreur lors de la restauration');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setRestoring(false);
      setProgress(0);
      setRestoreProgress('');
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const handleDelete = async (backup) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la sauvegarde ${backup.filename} ?`)) {
      return;
    }
    
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const response = await fetch(`https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=delete&file=${encodeURIComponent(backup.filename)}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        showToast('Sauvegarde supprimée avec succès', 'success');
        fetchBackups();
        if (selectedBackup?.filename === backup.filename) {
          setSelectedBackup(null);
          setPreview(null);
        }
      } else {
        throw new Error(result.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur:', err);
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const handleDownload = (backup) => {
    const userStr = localStorage.getItem('user');
    const user = JSON.parse(userStr);
    const userId = user.id || user.id_utilisateur;
    
    window.open(`https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=download&file=${encodeURIComponent(backup.filename)}`, '_blank');
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des sauvegardes</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Toaster />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, ${COLORS.amber})` }}>
                <i className="ri-history-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Restauration</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-database-2-line"></i> {backups.length} sauvegarde(s)
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.warning}20`, color: COLORS.warning }}>
                    <i className="ri-restart-line"></i> Restaurer une sauvegarde
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn-modern-outline" onClick={fetchBackups} disabled={loading}>
                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i> Actualiser
              </button>
              <button className="btn-modern-outline" onClick={() => navigate('/parametres/sauvegarde/manuel')}>
                <i className="ri-arrow-left-line"></i> Retour
              </button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="mb-3" style={{ borderLeft: `4px solid ${COLORS.danger}` }}>
              <div className="d-flex align-items-center">
                <i className="ri-error-warning-line me-2" style={{ color: COLORS.danger, fontSize: '1.2rem' }}></i>
                <span>{error}</span>
              </div>
            </Alert>
          )}

          {/* ===== BARRE DE PROGRESSION ===== */}
          {restoring && (
            <Row className="mb-4 fade-in">
              <Col md={12}>
                <Card className="progress-card-modern" style={{
                  border: 'none',
                  borderRadius: '0px',
                  background: 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                }}>
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold" style={{ color: COLORS.primaryDark }}>{restoreProgress}</span>
                      <span className="fw-bold" style={{ color: COLORS.warning }}>{progress}%</span>
                    </div>
                    <ProgressBar
                      now={progress}
                      variant="warning"
                      style={{
                        height: '6px',
                        borderRadius: '4px',
                        background: '#e9edf2'
                      }}
                    />
                    {restoreDetails && (
                      <div className="mt-2 small text-muted">
                        <i className="ri-check-line me-1 text-success"></i>
                        {restoreDetails.queries} requêtes exécutées
                        {restoreDetails.errors.length > 0 && (
                          <span className="ms-2 text-warning">
                            ({restoreDetails.errors.length} erreurs)
                          </span>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <Row className="g-4">
            {/* ===== LISTE DES SAUVEGARDES ===== */}
            <Col md={5}>
              <Card className="list-card-modern" style={{
                border: 'none',
                borderRadius: '0px',
                background: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                height: '100%'
              }}>
                <Card.Header style={{
                  background: 'rgba(255,255,255,0.5)',
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                  padding: '0.75rem 1.25rem'
                }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                      <i className="ri-database-2-line me-2" style={{ color: COLORS.primary }}></i>
                      Sauvegardes disponibles
                    </h6>
                    <button
                      className="btn-modern-outline-small"
                      onClick={() => setShowLogs(!showLogs)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: COLORS.info,
                        fontSize: '0.75rem',
                        padding: '0.2rem 0.6rem',
                        fontWeight: '500'
                      }}
                    >
                      <i className="ri-history-line me-1"></i>
                      {showLogs ? 'Masquer logs' : 'Voir logs'}
                    </button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {backups.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="ri-inbox-line" style={{ fontSize: '2.8rem', color: COLORS.primary, opacity: 0.25 }}></i>
                      <p className="mt-3 text-muted small">Aucune sauvegarde trouvée</p>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="rounded-pill"
                        onClick={() => navigate('/parametres/sauvegarde/manuel')}
                      >
                        Créer une sauvegarde
                      </Button>
                    </div>
                  ) : (
                    backups.map((backup, index) => (
                      <div
                        key={index}
                        className={`backup-item ${selectedBackup?.filename === backup.filename ? 'selected' : ''}`}
                        style={{
                          padding: '0.75rem 1.25rem',
                          borderBottom: '1px solid rgba(0,0,0,0.04)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          background: selectedBackup?.filename === backup.filename ? `${COLORS.primary}10` : 'transparent',
                          borderLeft: selectedBackup?.filename === backup.filename ? `3px solid ${COLORS.primary}` : '3px solid transparent'
                        }}
                        onClick={() => handlePreview(backup)}
                        onMouseEnter={(e) => {
                          if (selectedBackup?.filename !== backup.filename) {
                            e.currentTarget.style.background = '#f8fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedBackup?.filename !== backup.filename) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            background: `${COLORS.primary}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '0.75rem'
                          }}>
                            <i className="ri-file-zip-line" style={{ color: COLORS.primary }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="fw-medium small">{backup.filename}</div>
                            <div className="d-flex gap-3 small text-muted" style={{ fontSize: '0.7rem' }}>
                              <span><i className="ri-calendar-line me-1"></i>{formatDate(backup.date)}</span>
                              <span><i className="ri-hard-drive-line me-1"></i>{formatFileSize(backup.size)}</span>
                            </div>
                          </div>
                          <div>
                            <span className="badge rounded-pill" style={{
                              background: backup.type === 'manuel' ? COLORS.primary : COLORS.secondary,
                              color: 'white',
                              fontSize: '0.65rem',
                              padding: '0.2rem 0.6rem'
                            }}>
                              {backup.type === 'manuel' ? 'Manuelle' : 'Auto'}
                            </span>
                          </div>
                        </div>
                        <div className="d-flex justify-content-end gap-2 mt-1">
                          <button
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: COLORS.info,
                              fontSize: '0.8rem',
                              padding: '0.1rem 0.4rem'
                            }}
                            onClick={(e) => { e.stopPropagation(); handleDownload(backup); }}
                          >
                            <i className="ri-download-line"></i>
                          </button>
                          <button
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: COLORS.danger,
                              fontSize: '0.8rem',
                              padding: '0.1rem 0.4rem'
                            }}
                            onClick={(e) => { e.stopPropagation(); handleDelete(backup); }}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </Card.Body>
              </Card>

              {/* ===== LOGS ===== */}
              {showLogs && logs.length > 0 && (
                <Card className="mt-3" style={{
                  border: 'none',
                  borderRadius: '0px',
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                  overflow: 'hidden'
                }}>
                  <Card.Header style={{
                    background: 'rgba(255,255,255,0.5)',
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                    padding: '0.5rem 1.25rem'
                  }}>
                    <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.85rem' }}>
                      <i className="ri-history-line me-2" style={{ color: COLORS.primary }}></i>
                      Historique des restaurations
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {logs.map((log, index) => (
                      <div key={index} className="log-item" style={{
                        padding: '0.5rem 1.25rem',
                        borderBottom: '1px solid rgba(0,0,0,0.04)',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <i className="ri-restart-line me-2" style={{ color: COLORS.warning, fontSize: '0.8rem' }}></i>
                            <span className="small fw-medium">{log.filename}</span>
                          </div>
                          <span className="badge rounded-pill" style={{
                            background: log.status === 'success' ? COLORS.success : COLORS.warning,
                            color: 'white',
                            fontSize: '0.6rem',
                            padding: '0.2rem 0.5rem'
                          }}>
                            {log.status}
                          </span>
                        </div>
                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                          {formatDate(log.created_at)}
                        </small>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              )}
            </Col>

            {/* ===== APERÇU ===== */}
            <Col md={7}>
              <Card className="preview-card-modern" style={{
                border: 'none',
                borderRadius: '0px',
                background: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                height: '100%'
              }}>
                <Card.Header style={{
                  background: 'rgba(255,255,255,0.5)',
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                  padding: '0.75rem 1.25rem'
                }}>
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-eye-line me-2" style={{ color: COLORS.primary }}></i>
                    Aperçu de la sauvegarde
                  </h6>
                </Card.Header>
                <Card.Body>
                  {!selectedBackup ? (
                    <div className="text-center py-5">
                      <i className="ri-file-search-line" style={{ fontSize: '2.8rem', color: COLORS.primary, opacity: 0.25 }}></i>
                      <p className="mt-3 text-muted small">Sélectionnez une sauvegarde pour voir l'aperçu</p>
                    </div>
                  ) : !preview ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" style={{ width: '2rem', height: '2rem' }} />
                      <p className="mt-3 text-muted small">Chargement de l'aperçu...</p>
                    </div>
                  ) : (
                    <>
                      {/* ===== INFOS ===== */}
                      <div className="preview-info mb-4">
                        <Row className="g-2">
                          <Col md={6}>
                            <div style={{
                              background: '#f8fafc',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '4px'
                            }}>
                              <span className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nom du fichier</span>
                              <span className="fw-semibold small">{selectedBackup.filename}</span>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div style={{
                              background: '#f8fafc',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '4px'
                            }}>
                              <span className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date de création</span>
                              <span className="fw-semibold small">{formatDate(selectedBackup.date)}</span>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div style={{
                              background: '#f8fafc',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '4px'
                            }}>
                              <span className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Taille</span>
                              <span className="fw-semibold small">{formatFileSize(selectedBackup.size)}</span>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div style={{
                              background: '#f8fafc',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '4px'
                            }}>
                              <span className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</span>
                              <span className="fw-semibold small">
                                <span className="badge rounded-pill" style={{
                                  background: selectedBackup.type === 'manuel' ? COLORS.primary : COLORS.secondary,
                                  color: 'white',
                                  fontSize: '0.65rem'
                                }}>
                                  {selectedBackup.type === 'manuel' ? 'Manuelle' : 'Automatique'}
                                </span>
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {/* ===== TABLEAU ===== */}
                      <h6 className="fw-semibold mb-2" style={{ color: COLORS.primary, fontSize: '0.85rem' }}>
                        <i className="ri-file-copy-line me-2"></i>
                        Contenu de la sauvegarde ({preview.totalTables} tables, {preview.totalRows} enregistrements)
                      </h6>

                      <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        <Table hover size="sm" className="mb-0" style={{ fontSize: '0.8rem' }}>
                          <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                            <tr>
                              <th className="px-2 py-1" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Table</th>
                              <th className="px-2 py-1 text-center" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Enregistrements</th>
                              <th className="px-2 py-1 text-center" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>État</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.tables.map((table, index) => (
                              <tr key={index}>
                                <td className="px-2 py-1">
                                  <i className="ri-table-line me-2" style={{ color: COLORS.primary, fontSize: '0.8rem' }}></i>
                                  {table.name}
                                </td>
                                <td className="px-2 py-1 text-center">
                                  <span className="badge rounded-pill" style={{ background: COLORS.info, color: 'white', fontSize: '0.6rem' }}>
                                    {table.rows}
                                  </span>
                                </td>
                                <td className="px-2 py-1 text-center">
                                  <span className="badge rounded-pill" style={{ background: COLORS.success, color: 'white', fontSize: '0.6rem' }}>
                                    À restaurer
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {/* ===== STATISTIQUES ===== */}
                      <div className="preview-stats mt-3" style={{
                        background: `${COLORS.primary}10`,
                        padding: '0.75rem 1rem',
                        borderRadius: '4px'
                      }}>
                        <div className="d-flex justify-content-around">
                          <div className="text-center">
                            <div className="fw-bold" style={{ color: COLORS.primary, fontSize: '1.1rem' }}>{preview.totalTables || 0}</div>
                            <span className="text-muted" style={{ fontSize: '0.65rem' }}>Tables</span>
                          </div>
                          <div className="text-center">
                            <div className="fw-bold" style={{ color: COLORS.success, fontSize: '1.1rem' }}>{preview.totalRows || 0}</div>
                            <span className="text-muted" style={{ fontSize: '0.65rem' }}>Enregistrements</span>
                          </div>
                          <div className="text-center">
                            <div className="fw-bold" style={{ color: COLORS.warning, fontSize: '1.1rem' }}>{preview.totalSize || '0 MB'}</div>
                            <span className="text-muted" style={{ fontSize: '0.65rem' }}>Taille</span>
                          </div>
                        </div>
                      </div>

                      {/* ===== BOUTONS ===== */}
                      <div className="d-flex justify-content-end gap-2 mt-4">
                        <button
                          className="btn-modern-outline"
                          onClick={() => setShowDetailsModal(true)}
                          disabled={restoring}
                          style={{ fontSize: '0.8rem', padding: '0.4rem 1.2rem' }}
                        >
                          <i className="ri-information-line me-1"></i> Détails
                        </button>
                        <button
                          className="btn-modern-warning"
                          onClick={() => setShowConfirmModal(true)}
                          disabled={restoring}
                          style={{
                            background: `linear-gradient(135deg, ${COLORS.warning}, ${COLORS.amber})`,
                            border: 'none',
                            padding: '0.4rem 1.5rem',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            color: 'white',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s',
                            boxShadow: `0 4px 10px ${COLORS.warning}40`
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = `0 8px 16px ${COLORS.warning}50`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = `0 4px 10px ${COLORS.warning}40`;
                          }}
                        >
                          <i className="ri-history-line me-1"></i>
                          Restaurer cette sauvegarde
                        </button>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>

      {/* ===== MODALE DE CONFIRMATION ===== */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered className="delete-modal-modern">
        <Modal.Header
          closeButton
          className="border-0 pb-0"
          style={{
            background: `linear-gradient(135deg, ${COLORS.warning}, ${COLORS.amber})`,
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
          }}
        >
          <Modal.Title className="w-100 text-center">
            <div className="delete-icon-wrapper mb-2">
              <div className="delete-icon-circle" style={{
                width: '70px',
                height: '70px',
                borderRadius: '4px',
                background: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}>
                <i className="ri-alert-line" style={{ fontSize: '32px', color: 'white' }}></i>
              </div>
            </div>
            <h5 className="fw-bold text-white mb-0">⚠️ Confirmer la restauration</h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4 px-4">
          <p className="text-center text-muted mb-3">
            Vous êtes sur le point de restaurer la sauvegarde :
          </p>
          {selectedBackup && (
            <div className="p-3 mb-3" style={{
              background: '#f8fafc',
              borderLeft: `3px solid ${COLORS.primary}`,
              borderRadius: '4px'
            }}>
              <div className="fw-bold" style={{ color: COLORS.primaryDark }}>{selectedBackup.filename}</div>
              <div className="text-muted small">
                <i className="ri-calendar-line me-1"></i>
                {formatDate(selectedBackup.date)} • {formatFileSize(selectedBackup.size)}
              </div>
              {preview && (
                <div className="text-muted small">
                  {preview.totalTables} tables • {preview.totalRows} enregistrements
                </div>
              )}
            </div>
          )}
          <div style={{
            background: `${COLORS.danger}10`,
            border: `1px solid ${COLORS.danger}30`,
            borderRadius: '4px',
            padding: '0.8rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem'
          }}>
            <i className="ri-alert-line" style={{ color: COLORS.danger, fontSize: '1.3rem' }}></i>
            <span className="small text-muted">
              <strong>Attention :</strong> La restauration remplacera toutes les données actuelles par celles de la sauvegarde. Cette action est irréversible.
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
          <Button
            variant="light"
            onClick={() => setShowConfirmModal(false)}
            className="px-4"
            style={{
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              border: '1px solid #d1d5db',
              color: '#4b5563',
              fontWeight: '500',
              fontSize: '0.85rem',
              transition: 'all 0.2s',
              background: 'white'
            }}
          >
            Annuler
          </Button>
          <Button
            variant="warning"
            onClick={handleRestore}
            className="px-4"
            style={{
              background: `linear-gradient(135deg, ${COLORS.warning}, ${COLORS.amber})`,
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              fontWeight: '500',
              fontSize: '0.85rem',
              boxShadow: `0 4px 10px ${COLORS.warning}40`,
              transition: 'all 0.2s',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 8px 16px ${COLORS.warning}50`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 4px 10px ${COLORS.warning}40`;
            }}
          >
            <i className="ri-history-line me-2"></i>Confirmer la restauration
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== MODALE DE DÉTAILS ===== */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="lg" className="delete-modal-modern">
        <Modal.Header
          closeButton
          className="border-0 pb-0"
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})`,
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
          }}
        >
          <Modal.Title className="w-100 text-center">
            <h5 className="fw-bold text-white mb-0">
              <i className="ri-information-line me-2"></i>Détails de la sauvegarde
            </h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4 px-4">
          {selectedBackup && preview && (
            <>
              <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary, fontSize: '0.9rem' }}>Informations générales</h6>
              <Row className="g-2 mb-4">
                <Col md={6}>
                  <div className="p-2" style={{ background: '#f8fafc', borderRadius: '4px' }}>
                    <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Nom du fichier</small>
                    <strong className="small">{selectedBackup.filename}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-2" style={{ background: '#f8fafc', borderRadius: '4px' }}>
                    <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Date de création</small>
                    <strong className="small">{formatDate(selectedBackup.date)}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-2" style={{ background: '#f8fafc', borderRadius: '4px' }}>
                    <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Taille</small>
                    <strong className="small">{formatFileSize(selectedBackup.size)}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-2" style={{ background: '#f8fafc', borderRadius: '4px' }}>
                    <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Type</small>
                    <strong className="small">
                      <span className="badge rounded-pill" style={{
                        background: selectedBackup.type === 'manuel' ? COLORS.primary : COLORS.secondary,
                        color: 'white',
                        fontSize: '0.65rem'
                      }}>
                        {selectedBackup.type === 'manuel' ? 'Manuelle' : 'Automatique'}
                      </span>
                    </strong>
                  </div>
                </Col>
              </Row>

              <h6 className="fw-semibold mb-2" style={{ color: COLORS.primary, fontSize: '0.9rem' }}>Tables incluses</h6>
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                <Table hover size="sm" className="mb-0" style={{ fontSize: '0.8rem' }}>
                  <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                    <tr>
                      <th className="px-2 py-1" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>#</th>
                      <th className="px-2 py-1" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Nom de la table</th>
                      <th className="px-2 py-1 text-center" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Enregistrements</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.tables.map((table, index) => (
                      <tr key={index}>
                        <td className="px-2 py-1">{index + 1}</td>
                        <td className="px-2 py-1">{table.name}</td>
                        <td className="px-2 py-1 text-center">
                          <span className="badge rounded-pill" style={{ background: COLORS.info, color: 'white', fontSize: '0.6rem' }}>
                            {table.rows}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="mt-3 p-3" style={{
                background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
                borderRadius: '4px'
              }}>
                <Row className="text-center">
                  <Col>
                    <div className="fw-bold" style={{ color: COLORS.primary, fontSize: '1.1rem' }}>{preview.totalTables}</div>
                    <small className="text-muted">Tables</small>
                  </Col>
                  <Col>
                    <div className="fw-bold" style={{ color: COLORS.success, fontSize: '1.1rem' }}>{preview.totalRows}</div>
                    <small className="text-muted">Enregistrements</small>
                  </Col>
                  <Col>
                    <div className="fw-bold" style={{ color: COLORS.warning, fontSize: '1.1rem' }}>{preview.totalSize}</div>
                    <small className="text-muted">Taille</small>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center">
          <Button
            variant="light"
            onClick={() => setShowDetailsModal(false)}
            style={{
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              border: '1px solid #d1d5db',
              color: '#4b5563',
              fontWeight: '500',
              fontSize: '0.85rem',
              transition: 'all 0.2s',
              background: 'white'
            }}
          >
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== STYLES ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }

        .progress-card-modern {
          border: none;
          border-radius: 0px;
          background: rgba(255,255,255,0.8);
          backdropFilter: 'blur(8px)';
          box-shadow: 0 4px 12px rgba(0,0,0,0.03);
        }

        .list-card-modern, .preview-card-modern {
          transition: all 0.3s ease;
        }
        .list-card-modern:hover, .preview-card-modern:hover {
          box-shadow: 0 16px 40px rgba(0,0,0,0.08) !important;
        }

        .backup-item {
          transition: all 0.2s ease;
        }

        .delete-modal-modern .modal-content {
          border: none;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }

        .btn-modern-warning {
          transition: all 0.2s ease;
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </>
  );
}